import React, {
  useState, useEffect, useMemo, useContext,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from 'Context';
import { Pathname } from 'Routes';
import NetflixPlayer from 'Components/NetflixPlayer';
import { VideoUploadModal } from 'Components/VideoUploadModal';
import {
  overlay, center, card, heading, body,
  primaryBtn, secondaryBtn, ctaRow, isMobileQuery,
} from './styles';

// All debug logs use this prefix so they're filterable in DevTools
// (`>>> [interview-upload]` in the console search box).
const LOG = '[interview-upload]';

// Review the just-recorded interview before it leaves the device.
//
// Routing contract:
//   navigate('/interview/review', { state: { file, nextPath }, replace: true })
//
// UX:
//   • Save to my Projector — mounts <VideoUploadModal> inline with the
//     recorded File as `initialFile`. The modal handles thumbnails,
//     the metadata form, and the upload pipeline; we just keep it on
//     screen until the user dismisses it.
//   • Re-record — navigate back to /interview/host.
//   • Not now — continue to nextPath.
//
// We mount the modal INLINE (not by navigating to /profile and relying
// on Header to render the modal) because the cross-route hand-off had
// timing pitfalls — pendingVideoFile context updates can race with the
// modal's mount + cleanup, and the modal would end up on the picker
// screen instead of the file-details screen. Inline mount is simpler
// and the timing is local.
export const InterviewReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useContext(AppContext);
  const file = location.state && location.state.file;
  const nextPath = (location.state && location.state.nextPath) || Pathname.profile.basePath;

  const [isMobile, setIsMobile] = useState(isMobileQuery);
  useEffect(() => {
    const onResize = () => setIsMobile(isMobileQuery());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Controls the inline VideoUploadModal. Flipped on Save; flipped off
  // by the modal's onClose, which then navigates the user onward.
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Build a stable preview URL for the File. Revoke on unmount so we
  // don't leak the Blob.
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  useEffect(() => () => {
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (e) { /* noop */ }
    }
  }, [previewUrl]);

  // One mount-time diagnostic so we can confirm the page received the
  // file via location.state (vs. a hard-refresh deep-link that bypasses
  // history.state and lands here with no file).
  useEffect(() => {
    if (!file) {
      // eslint-disable-next-line no-console
      console.warn(`${LOG} mounted WITHOUT a file — location.state.file is missing`);
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`${LOG} mounted with file`, {
      name: file.name,
      size: file.size,
      type: file.type,
      ext: file.name.toLowerCase().slice(file.name.lastIndexOf('.')),
    });
  }, [file]);

  // Defensive: hitting /interview/review directly with no file means a
  // refresh or deep-link. Nothing to do here — send them home.
  if (!file) {
    return (
      <div style={overlay}>
        <div style={center(isMobile)}>
          <div style={card(isMobile)}>
            <h1 style={heading(isMobile)}>No recording to review</h1>
            <p style={body}>
              We couldn&apos;t find a recording. If your session was interrupted,
              you&apos;ll need to record again.
            </p>
            <div style={ctaRow}>
              <button
                type="button"
                onClick={() => navigate(nextPath, { replace: true })}
                style={primaryBtn(false)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = () => {
    if (!file) return;
    // eslint-disable-next-line no-console
    console.log(`${LOG} Save clicked — opening inline VideoUploadModal`, {
      fileName: file.name,
      fileSize: file.size,
      hasUserSubscription: !!(userDetails && userDetails.isSubscription),
    });
    setShowUploadModal(true);
  };

  const handleUploadClose = () => {
    // eslint-disable-next-line no-console
    console.log(`${LOG} VideoUploadModal closed — navigating onward to`, nextPath);
    setShowUploadModal(false);
    // After the modal closes (either upload submitted or user bailed),
    // continue the onboarding flow. The upload runs in the background
    // via uploadManagerSecure; nothing here needs to wait for it.
    navigate(nextPath, { replace: true });
  };

  const handleReRecord = () => {
    // eslint-disable-next-line no-console
    console.log(`${LOG} Re-record clicked`);
    navigate(Pathname.interview.host, { state: { nextPath }, replace: true });
  };

  const handleSkip = () => {
    // eslint-disable-next-line no-console
    console.log(`${LOG} Skip clicked — navigating to`, nextPath);
    navigate(nextPath, { replace: true });
  };

  const sizeLabel = (() => {
    const bytes = file.size || 0;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  })();

  return (
    <div style={overlay}>
      <div style={center(isMobile)}>
        <div style={card(isMobile)}>
          <h1 style={heading(isMobile)}>Your recording</h1>
          <p style={body}>
            Take a quick look. When it&apos;s right, save it to your Projector.
          </p>

          <div
            style={{
              width: '100%',
              borderRadius: '14px',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.10)',
              background: '#000',
            }}
          >
            <NetflixPlayer
              src={previewUrl}
              fillHeight={false}
              autoPlay={false}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              color: '#cfd1da',
              fontSize: '0.85rem',
            }}
          >
            <span>{file.name}</span>
            <span aria-hidden="true">·</span>
            <span>{sizeLabel}</span>
          </div>

          <div style={ctaRow}>
            <button type="button" onClick={handleReRecord} style={secondaryBtn}>
              Re-record
            </button>
            <button type="button" onClick={handleSkip} style={secondaryBtn}>
              Not now
            </button>
            <button type="button" onClick={handleSave} style={primaryBtn(false)}>
              Save to my Projector
            </button>
          </div>
        </div>
      </div>

      {/* Inline upload modal. initialFile bypasses the AppContext
          pendingVideoFile dance — VideoUploadModal reads it on mount,
          synthesizes a file-select event, and proceeds straight to the
          metadata + upload screen. */}
      {showUploadModal && (
        <VideoUploadModal
          open={showUploadModal}
          onClose={handleUploadClose}
          initialFile={file}
          userDataProp={{
            subscription: (userDetails && userDetails.isSubscription) || null,
            totalContentUploaded: (userDetails && userDetails.totalContentUploaded) || 0,
          }}
        />
      )}
    </div>
  );
};

export default InterviewReview;
